import { dataSourceFlags } from '@/app/dataSourceFlags';
import { getDevMockInternalUser, updateDevMockInternalUser } from '@/app/devMode';
import { type IUserProfile, type IUserCareerGoal } from '@/models/userProfile';
import { userProfileSchema } from '@/schemas/api/user';
import { api, publicApi } from '../api';

export interface UpdateUserProfileInput {
  userId: string;
  title: string;
  company: string;
  linkedin: string;
  name: string;
  email2: string;
  careerGoals?: IUserCareerGoal | IUserCareerGoal[];
}

/**
 * Handles the switching between live API calls and local dev-mode mocks
 * for all user-related data operations.
 */
export const userTransport = {
  async fetchProfile(userId: string): Promise<IUserProfile> {
    if (!dataSourceFlags.useLiveUsers) {
      return userProfileSchema.parse(getDevMockInternalUser());
    }
    const response = await api.get<IUserProfile>('/users/profile', { params: { userId } });
    return userProfileSchema.parse(response.data);
  },

  async fetchSessionLeader(meetingId: string): Promise<IUserProfile> {
    if (!dataSourceFlags.useLiveUsers) {
      return userProfileSchema.parse(getDevMockInternalUser());
    }
    const response = await api.get<IUserProfile>('/users/meeting', { params: { meetingId } });
    return userProfileSchema.parse(response.data);
  },

  async updateProfile(profile: UpdateUserProfileInput): Promise<IUserProfile> {
    if (!dataSourceFlags.useLiveUsers) {
      const data = updateDevMockInternalUser({
        name: profile.name,
        title: profile.title,
        company: profile.company,
        linkedin: profile.linkedin,
        email2: profile.email2,
        careerGoals: profile.careerGoals
      });
      return userProfileSchema.parse(data);
    }
    const response = await api.patch<IUserProfile>('/users', profile);
    return userProfileSchema.parse(response.data);
  },

  async fetchInternalUser(authId: string, email: string): Promise<IUserProfile> {
    if (!dataSourceFlags.useLiveUsers) {
      return userProfileSchema.parse(getDevMockInternalUser());
    }
    const response = await api.get<IUserProfile>('/users', { params: { authId, email } });
    return userProfileSchema.parse(response.data);
  },

  async uploadAvatar(userId: string, file: Blob): Promise<IUserProfile> {
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('file', file, 'avatar.png');
    const response = await api.post<IUserProfile>('/users/avatar', formData);
    return userProfileSchema.parse(response.data);
  },

  async deleteAvatar(userId: string): Promise<IUserProfile> {
    const response = await api.delete<IUserProfile>('/users/avatar', { params: { userId } });
    return userProfileSchema.parse(response.data);
  },

  /** Checks if a user already exists with the given email. */
  async checkUserExists(email: string): Promise<boolean> {
    if (!dataSourceFlags.useLiveUsers) {
      // WHY: Allow testing both branches in dev/test by using a reserved email.
      return email === 'existing@test.com';
    }
    const response = await publicApi.get<boolean>(`/users/check-email/${encodeURIComponent(email)}`);
    return response.data;
  }
};
